// CreateTeamDialog — admin-side manual team creator (PRODOPSAI-540).
//
// Required inputs: `team_key` (admin-pickable, must be unique against
// raw_dim_team.team_key), `team_name`. Optional: `region_name`, `productId`
// (PRODOPSAI-542 — sourced from productApi.getAll(), one-fetch-per-open).
// Native `<dialog>` lives in `@/components/ui/Dialog` (S6819 — never
// `role="dialog"` on a `<div>`). Helpers in `team-form-helpers.ts` keep this
// component below SonarCloud S3776.

import { useEffect, useState } from 'react';
import type { ITeam } from '@skystone/shared';
import {
  Button,
  Dialog,
  DialogFooter,
  ErrorBanner,
} from '@/components/ui';
import { inputClass } from '@/lib/form-styles';
import { cn, getErrorMessage } from '@/lib/utils';
import { teamApi } from '@/services/team-api';
import {
  REGION_NAME_MAX,
  TEAM_KEY_MAX,
  TEAM_NAME_MAX,
  buildCreateBody,
  canSaveCreate,
  isConflictError,
} from './team-form-helpers';
import { TeamProductPickerField } from './TeamProductPickerField';

interface CreateTeamDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: (created: ITeam) => void;
}

export function CreateTeamDialog({ open, onClose, onSaved }: Readonly<CreateTeamDialogProps>) {
  const [teamKey, setTeamKey] = useState<string>('');
  const [teamName, setTeamName] = useState<string>('');
  const [regionName, setRegionName] = useState<string>('');
  const [productId, setProductId] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [keyConflict, setKeyConflict] = useState<string | null>(null);

  // Reset whenever the dialog closes so a re-open starts fresh.
  useEffect(() => {
    if (open === true) return;
    setTeamKey('');
    setTeamName('');
    setRegionName('');
    setProductId('');
    setSaveError(null);
    setKeyConflict(null);
    setSaving(false);
  }, [open]);

  const saveable = canSaveCreate({ teamKey, teamName }) && saving === false;

  const handleSave = async () => {
    if (saveable === false) return;
    setSaving(true);
    setSaveError(null);
    setKeyConflict(null);
    try {
      const body = buildCreateBody({ teamKey, teamName, regionName, productId });
      const created = await teamApi.create(body);
      onSaved(created);
      onClose();
    } catch (err: unknown) {
      if (isConflictError(err) === true) {
        setKeyConflict('A team with this team_key already exists. Pick a different key.');
      } else {
        setSaveError(getErrorMessage(err, 'Failed to create team'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="Add team" maxWidth="max-w-lg">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSave();
        }}
        data-testid="create-team-form"
      >
        <div>
          <label htmlFor="create-team-key" className="mb-1 block text-sm font-medium">
            Team key <span className="text-error-500">*</span>
          </label>
          <input
            id="create-team-key"
            type="text"
            value={teamKey}
            onChange={(e) => {
              setTeamKey(e.target.value);
              setKeyConflict(null);
            }}
            maxLength={TEAM_KEY_MAX + 10}
            placeholder="e.g. se-team-platform-support"
            className={cn(inputClass, keyConflict !== null && 'border-error-500')}
            data-testid="create-team-key"
            autoFocus
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Pick anything unique. If Fabric later sends a team with the same key, the team
            becomes Fabric-managed (this is intentional).
          </p>
          {keyConflict !== null && (
            <p
              className="mt-1 text-xs text-error-600"
              role="alert"
              data-testid="create-team-key-conflict"
            >
              {keyConflict}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="create-team-name" className="mb-1 block text-sm font-medium">
            Team name <span className="text-error-500">*</span>
          </label>
          <input
            id="create-team-name"
            type="text"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            maxLength={TEAM_NAME_MAX + 10}
            placeholder="e.g. SE Team Platform Support"
            className={inputClass}
            data-testid="create-team-name"
          />
        </div>

        <div>
          <label htmlFor="create-team-region" className="mb-1 block text-sm font-medium">
            Region
          </label>
          <input
            id="create-team-region"
            type="text"
            value={regionName}
            onChange={(e) => setRegionName(e.target.value)}
            maxLength={REGION_NAME_MAX + 10}
            placeholder="e.g. SE"
            className={inputClass}
            data-testid="create-team-region"
          />
        </div>

        {/* PRODOPSAI-542: product picker extracted to a shared field (CPD).
            Dialog returns null when !open, so this remounts on each open,
            triggering one productApi.getAll() per session. */}
        <TeamProductPickerField
          value={productId}
          onChange={setProductId}
          idPrefix="create-team"
        />

        {saveError !== null && <ErrorBanner message={saveError} />}

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={saving}
            disabled={saveable === false}
            data-testid="create-team-save"
          >
            Create team
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
